import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getAccessToken } from 'src/api/axiosClient/tokenStorage';
import { useSignupApi } from '../api';
import { isTransientFailure } from '../helpers/apiErrors';
import { setSignupToken } from '../helpers/signupSession';
import { SignupPlanNotice } from '../types';

const SESSION_TOKEN_PARAM = 't';

const PLAN_ROUTE = '/signup';

/**
 * Every arrival is a navigation except two. A funnel that expired after its account was created has nowhere to
 * go: the plan list would offer a second signup next to the account it already has. And a funnel that could
 * not be read is not a funnel that is gone, so the answer is another try rather than the plan list.
 */
export type SignupResumeOutcome = 'working' | 'expiredWithAccount' | 'unavailable';

export const useSignupResumeViewController = () => {
  const api = useSignupApi();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const sessionToken = searchParams.get(SESSION_TOKEN_PARAM);

  const [outcome, setOutcome] = useState<SignupResumeOutcome>('working');
  const [attempt, setAttempt] = useState(0);

  // One attempt per mount, whatever the router does to this effect's dependencies in between.
  const attempted = useRef(false);

  const retry = useCallback(() => {
    attempted.current = false;
    setOutcome('working');
    setAttempt(current => current + 1);
  }, []);

  useEffect(() => {
    if (attempted.current) return;
    attempted.current = true;

    const toPlans = (notice?: SignupPlanNotice) =>
      navigate(PLAN_ROUTE, { replace: true, state: notice == null ? undefined : { signupNotice: notice } });

    if (sessionToken == null || sessionToken.length === 0) {
      toPlans();
      return;
    }

    api.getStateWithToken(sessionToken)
      .then(({ data }) => {
        /*
          Asked before the account below, and of the funnel rather than of `companyCreated`: the company row is
          written part-way through provisioning, so that flag can be set with the form still open and the
          account still half-built — and the login screen is no way back to the form that finishes it.
        */
        if (data.allowedActions.includes('save_company_info')) {
          setSignupToken(sessionToken);
          navigate('/signup/company', { replace: true });
          return;
        }

        // The session token stops standing in for a password the moment the account exists.
        if (data.companyCreated) {
          // A page load, not a route change: the placing happens in `AuthContext` on startup, and a
          // client-side hop would land before it runs.
          if (getAccessToken() != null) {
            window.location.href = PLAN_ROUTE;
            return;
          }
          // Answered here rather than on the login form, where nothing would say what became of the signup.
          if (data.status === 'expired') {
            setOutcome('expiredWithAccount');
            return;
          }
          navigate('/login', { replace: true });
          return;
        }

        if (data.status === 'expired') {
          toPlans('expired');
          return;
        }

        // Stored only now that the API has recognised it, and overwriting whatever funnel this browser held —
        // which is what following a link to a particular funnel asks for.
        //
        // A row on the account step has an address and no code sent — the send is a call of its own. The
        // screen reads the name and the address back, so nothing there is asked for twice.
        if (data.step === 'account') {
          setSignupToken(sessionToken);
          navigate('/signup/account', { replace: true });
          return;
        }
        // The flag is not optional: checkpoints are whole hours and a code dies after thirty minutes, so the
        // email this client followed is spent and the code screen must not open asking for its six digits.
        if (data.step === 'verify_email') {
          setSignupToken(sessionToken);
          navigate('/signup/verify', { replace: true, state: { signupCodeStale: true } });
          return;
        }

        // Nothing was lost worth reporting: for a funnel still on the plan step, this screen is that step.
        toPlans();
      })
      .catch((error: unknown) => {
        // Only a definitive answer condemns the link. A timeout reaching that line would send somebody whose
        // funnel is sitting there untouched off to start a second one.
        if (isTransientFailure(error)) {
          setOutcome('unavailable');
          return;
        }
        toPlans('linkDead');
      });
  }, [api, attempt, navigate, sessionToken]);

  const goToLogin = useCallback(() => navigate('/login'), [navigate]);

  return { outcome: outcome, goToLogin: goToLogin, retry: retry };
};
